import React from 'react';
import type { CustomBriefConclusion, SiteRagReport } from '../../../../types/siteRag';
import { FindingClaimsList } from '../../report/FindingClaimsList';
import { walkCitationText } from '../../../../lib/walkCitationText';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialBadge } from '../../report/editorial';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

interface Props {
  report: SiteRagReport;
  conclusion: CustomBriefConclusion;
}

const MONO = 'var(--report-body-font, inherit)';

const PRIORITY_LABELS: Record<string, string> = {
  immediate: 'Immediate',
  'pre-FID': 'Pre-FID',
  'pre-close': 'Pre-close',
};

/** Small-caps mono section label, themed via roleColor('muted'). */
const Heading: React.FC<{ theme: ReturnType<typeof useEffectiveReportTheme>; children: React.ReactNode }> = ({
  theme,
  children,
}) => {
  // Sizing tracks sectionBlockChrome(theme).defLabelStyle by headingScale.
  const defLabelStyle: React.CSSProperties =
    theme.headingScale === 'compact'
      ? { fontSize: REPORT_TEXT_PX.micro, letterSpacing: '0.1em' }
      : theme.headingScale === 'technical'
        ? { fontSize: REPORT_TEXT_PX.caption, letterSpacing: '0.12em' }
        : { fontSize: 10.5, letterSpacing: '0.14em' };
  return (
    <h3
      className="font-semibold uppercase"
      style={{ fontFamily: MONO, color: roleColor(theme, 'muted'), ...defLabelStyle }}
    >
      {children}
    </h3>
  );
};

export const CustomBriefConclusionBody: React.FC<Props> = ({ report, conclusion }) => {
  const theme = useEffectiveReportTheme();
  return (
    <div className="space-y-6" data-testid="custom-brief-conclusion">
      <div className={`grid grid-cols-1 gap-6 ${theme.bodyColumns === 2 ? '' : 'md:grid-cols-2'}`}>
        {conclusion.topStrengths.length > 0 && (
          <div>
            <Heading theme={theme}>Top strengths</Heading>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm" style={{ color: roleColor(theme, 'ink') }}>
              {conclusion.topStrengths.map((s, i) => (
                <li key={i}>{walkCitationText(s, report.references)}</li>
              ))}
            </ul>
          </div>
        )}
        {conclusion.keyRisks.length > 0 && (
          <FindingClaimsList
            entries={conclusion.keyRisks}
            references={report.references}
            siteId={report.siteId}
            reportId={report.id}
            sectionId="customBriefConclusion"
            responses={report.openQuestionResponses}
            redFlagsHeading="Key risks (confirmed)"
            className="space-y-4"
          />
        )}
      </div>
      {conclusion.recommendedNextActions.length > 0 && (
        <div>
          <Heading theme={theme}>Recommended next actions</Heading>
          <ol className="mt-3 space-y-3">
            {conclusion.recommendedNextActions.map((item, i) => (
              <li
                key={i}
                className="pt-3 text-sm"
                style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }}
              >
                <div className="mb-1 flex items-center gap-2">
                  <EditorialBadge theme={theme} tone="accent" variant="outline">
                    {PRIORITY_LABELS[item.priority] ?? item.priority}
                  </EditorialBadge>
                  <span className="font-medium" style={{ color: roleColor(theme, 'ink') }}>
                    {walkCitationText(item.action, report.references)}
                  </span>
                </div>
                {item.rationale.trim() && (
                  <p className="text-xs leading-relaxed" style={{ color: roleColor(theme, 'muted') }}>
                    {walkCitationText(item.rationale, report.references)}
                  </p>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};
